import React, { useEffect, useRef, useState, useCallback } from "react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function decodeJwt(token) {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
				.join(""),
		);
		return JSON.parse(jsonPayload);
	} catch (err) {
		console.error("Failed to decode JWT:", err);
		return null;
	}
}

function loadGoogleScript() {
	return new Promise((resolve, reject) => {
		if (window.google?.accounts?.id) {
			resolve();
			return;
		}
		const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
		if (existing) {
			existing.addEventListener("load", () => resolve());
			existing.addEventListener("error", reject);
			return;
		}
		const script = document.createElement("script");
		script.src = GIS_SCRIPT_SRC;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = reject;
		document.head.appendChild(script);
	});
}

export default function GoogleLoginButton({
	onLogin,
	onLogout,
	buttonProps = {},
}) {
	const buttonRef = useRef(null);
	const [user, setUser] = useState(null);
	const [error, setError] = useState(null);
	const [scriptReady, setScriptReady] = useState(false);

	const handleCredentialResponse = useCallback(
		(response) => {
			const credential = response?.credential;
			if (!credential) {
				setError("No credential received from Google.");
				return;
			}
			const payload = decodeJwt(credential);
			if (!payload) {
				setError("Could not decode login response.");
				return;
			}
			if (!payload.email_verified) {
				setError("This Google account's email is not verified.");
				return;
			}
			const userInfo = {
				email: payload.email,
				email_verified: payload.email_verified,
				name: payload.name,
				picture: payload.picture,
				sub: payload.sub, 
			};
			setError(null);
			setUser(userInfo);
			onLogin?.(userInfo, credential);
		},
		[onLogin],
	);

	useEffect(() => {
		if (!GOOGLE_CLIENT_ID) {
			setError("Missing REACT_APP_GOOGLE_CLIENT_ID env variable.");
			return;
		}

		let cancelled = false;

		loadGoogleScript()
			.then(() => {
				if (cancelled) return;
				window.google.accounts.id.initialize({
					client_id: GOOGLE_CLIENT_ID,
					callback: handleCredentialResponse,
					auto_select: false,
				});
				setScriptReady(true);
			})
			.catch(() => {
				if (!cancelled) setError("Failed to load Google Sign-In script.");
			});

		return () => {
			cancelled = true;
		};
	}, [handleCredentialResponse]);

	useEffect(() => {
		if (scriptReady && buttonRef.current && !user) {
			window.google.accounts.id.renderButton(buttonRef.current, {
				theme: "outline",
				size: "large",
				type: "standard",
				shape: "rectangular",
				text: "signin_with",
				logo_alignment: "left",
				width: 280,
				...buttonProps,
			});
		}
	}, [scriptReady, user, buttonProps]);

	const handleLogout = () => {
		window.google?.accounts?.id?.disableAutoSelect();
		setUser(null);
		onLogout?.();
	};

	if (user) {
		return (
			<div style={styles.loggedInWrap}>
				{user.picture && (
					<img src={user.picture} alt={user.name} style={styles.avatar} />
				)}
				<div>
					<div style={styles.name}>{user.name}</div>
					<div style={styles.email}>
						{user.email} <span style={styles.verifiedBadge}>✓ verified</span>
					</div>
				</div>
				{/* <button onClick={handleLogout} style={styles.logoutBtn}>
          Logout
        </button> */}
			</div>
		);
	}

	return (
		<div>
			<div ref={buttonRef} />
			{error && <div style={styles.error}>{error}</div>}
		</div>
	);
}

const styles = {
	loggedInWrap: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "8px 12px",
		border: "1px solid #e0e0e0",
		borderRadius: 8,
		maxWidth: 360,
	},
	avatar: { width: 40, height: 40, borderRadius: "50%" },
	name: { fontWeight: 600, fontSize: 14 },
	email: { fontSize: 12, color: "#555" },
	verifiedBadge: { color: "#1a7f37", fontSize: 11 },
	logoutBtn: {
		marginLeft: "auto",
		padding: "6px 12px",
		fontSize: 13,
		border: "1px solid #ccc",
		borderRadius: 6,
		background: "#fff",
		cursor: "pointer",
	},
	error: { color: "#c00", fontSize: 13, marginTop: 8 },
};
